import { useQuery } from '@tanstack/react-query'

import type { RelatorioFinanceiro, SerieFinanceira } from '../types'
import type { FiltroPeriodo } from '@/features/relatorio/api/relatorio-api'

import { api } from '@/lib/api-client'

function paramsDoFiltro(filtro: FiltroPeriodo): Record<string, string> {
  if (filtro.inicio && filtro.fim) return { inicio: filtro.inicio, fim: filtro.fim }
  return { preset: filtro.preset ?? 'mes' }
}

// Cache do cliente: revisitar um período já visto (voltar/trocar de aba) não
// refaz a busca por 5 min — o servidor já cacheia, então isto evita até o
// round-trip. A borda lenta é só o 1º acesso de cada período.
const STALE_MS = 5 * 60_000

export function useFinanceiro(filtro: FiltroPeriodo) {
  const params = paramsDoFiltro(filtro)

  return useQuery({
    queryKey: ['financeiro', params],
    queryFn: async () => {
      const { data } = await api.get<RelatorioFinanceiro>('/api/relatorio/financeiro', { params })
      return data
    },
    // Ao trocar o período, mantém o painel anterior enquanto o novo carrega —
    // sem flash de skeleton. O `isFetching` sinaliza o carregamento na UI.
    placeholderData: (anterior) => anterior,
    staleTime: STALE_MS,
  })
}

export function useSerieFinanceira(meses = 6) {
  return useQuery({
    queryKey: ['financeiro', 'serie', meses],
    queryFn: async () => {
      const { data } = await api.get<SerieFinanceira>('/api/relatorio/financeiro/serie', {
        params: { meses },
      })
      return data
    },
    staleTime: STALE_MS,
  })
}
